import { useNavigate } from "react-router-dom";
import { FileText, Info, Download, Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, X, TrendingUp, TrendingDown, MapPin, Map, Grid3X3, Target, Crosshair, IndianRupee, ArrowRightLeft, Clock, Landmark, Wallet, Scale, ArrowUpRight, Sparkles, Users } from "lucide-react";
import IndiaMap from "../components/IndiaMap";
import ThemeChip from "../components/ThemeChip";
import { fmtCr, fmtNum, displayTheme } from "../utils/format";
import { useCommandCentreData } from "../hooks/useCommandCentreData";
import { useAuth } from "../context/AuthContext";

const PIE_COLORS = [
  "#B71C1C", // deep red       — Cancer Care
  "#16A34A", // green          — Rural Upliftment
  "#F59E0B", // amber/yellow   — Education
  "#475569", // slate gray     — Institutions
  "#8B5CF6", // violet         — Health
  "#0EA5E9", // sky blue       — Nutrition
  "#EA580C", // burnt orange   — Others
  "#0D9488", // teal           — Water & Sanitation
  "#DB2777", // pink / magenta — Urban Poverty
  "#4F46E5", // indigo         — Arts, Culture
];

/* ─── YoY Delta Badge ───────────────────────────────────────────── */
function YoyBadge({ delta, prevFy }) {
  if (!delta || delta.pct === null || delta.pct === undefined) return null;
  const pct = delta.pct;
  const isUp = pct > 0;
  const isDown = pct < 0;
  const isFlat = pct === 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : null;
  const color = isUp ? "text-green-600" : isDown ? "text-red-500" : "text-slate-600";
  const bg = isUp ? "bg-green-50" : isDown ? "bg-red-50" : "bg-slate-50";
  const label = `${isUp ? "+" : ""}${pct.toFixed(1)}%`;
  return (
    <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${bg} ${color}`} data-testid="yoy-badge" title={`vs ${prevFy || "last FY"}`}>
      {Icon && <Icon size={10} strokeWidth={2} />}
      <span className="text-[9px] font-semibold leading-none">{isFlat ? "0%" : label}</span>
    </div>
  );
}

export default function CommandCentre() {
  const navigate = useNavigate();
  const {
    summary, geoSummary, stateData, allocationByTheme,
    ragSummary, loading, sf, s, hasGrants, g, totalSanctioned,
    selectedState, selectedDistrict, districtData, blockData, mapLoading,
    handleStateSelect, handleDistrictSelect,
    regionSummary, regionGeoSummary, regionRagSummary,
    aspirationalData,
    sankeyData,
    mapViewMode, setMapViewMode, allDistrictData,
    yoyData,
    grants, grantsLoading, grantsPage, setGrantsPage, grantsTotal,
    grantsTotalPages, grantsSearchInput, setGrantsSearchInput,
    grantsSortBy, grantsSortOrder, grantsExporting,
    handleGrantsSort, handleGrantsSearch, clearGrantsSearch,
    handleGrantsExport, scrollToGrantsTable, grantsTableRef,
    grantsTableOpen, setGrantsTableOpen,
    filters, filterOptions, viewMode, API,
  } = useCommandCentreData();

  const statusClass = (s) =>
    s === "Active" || s === "Ongoing" || s === "Under Closure" ? "bg-green-100 text-green-700" :
    s === "Closed" ? "bg-slate-100 text-slate-500" :
    "bg-amber-100 text-amber-700";

  // Only show YoY badges when a specific FY is selected
  const yoy = filters.fy ? yoyData : null;

  // Personalised greeting (time-of-day aware) — shown only on first paint per session
  const { user } = useAuth();
  const firstName = (user?.name || user?.email || "").split(/[\s@]/)[0] || "";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–";

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
      </div>
    );
  }

  return (
    <div data-testid="command-centre" className="space-y-3">
      {/* Welcome greeting — subtle, personalised, time-aware */}
      {firstName && (
        <div
          data-testid="welcome-greeting"
          className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-red-50/70 via-white to-white border border-red-100 rounded-lg animate-fade-in"
        >
          <Sparkles size={13} className="text-[#B71C1C] flex-shrink-0" />
          <span className="text-xs text-slate-700">
            <span className="font-medium text-slate-900">{greeting}, {firstName}.</span>
            <span className="ml-1 text-slate-500">Here's what's happening across your portfolio today.</span>
          </span>
        </div>
      )}

      {/* Main Content: Map + Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-3 items-stretch">

        {/* LEFT: Map + Sanctioned-by-Theme vertical bar chart (stacked) */}
        <div className="min-w-0 overflow-hidden flex flex-col gap-3">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-3 overflow-hidden flex justify-center">
              {sf ? (
                <IndiaMap
                  stateData={stateData}
                  districtData={districtData}
                  blockData={blockData}
                  allDistrictData={allDistrictData}
                  onStateSelect={handleStateSelect}
                  onDistrictSelect={handleDistrictSelect}
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  dataLoading={mapLoading}
                  mapViewMode={mapViewMode}
                  onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) handleStateSelect(null); }}
                />
              ) : (
                <div className="h-64 flex items-center justify-center bg-slate-50 rounded-lg text-slate-600">
                  Map requires data connection
                </div>
              )}
            </div>
          </div>

          {/* Sanctioned by Theme — vertical bar chart (sits below map, fills remaining height) */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex-1 flex flex-col" data-testid="allocation-by-theme-section">
            <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-baseline justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{sf ? "Sanctioned by Theme" : "Disbursement by Theme"}</h3>
                <p className="text-xs text-slate-500 mt-0.5">% share of portfolio · {`₹${totalSanctioned.toFixed(2)} Cr total`}</p>
              </div>
            </div>
            <div className="p-4 flex-1 min-h-0">
              {hasGrants && allocationByTheme.length > 0 ? (
                <ThemePortfolioBars data={allocationByTheme} sf={sf} />
              ) : <div className="py-8 text-center text-sm text-slate-600">{loading ? "Loading..." : "No data for selected filters"}</div>}
            </div>
          </div>
        </div>

        {/* RIGHT: Metrics Panel (1/3 width) — compact */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-3 flex flex-col gap-2">

            {/* Grants Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Grants</h3>
                <button onClick={scrollToGrantsTable} className="text-[10px] text-[#B71C1C] hover:underline" data-testid="grants-view-scroll">View →</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-[#B71C1C]/30 transition-colors"
                  data-testid="kpi-grants-approved"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#B71C1C] flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.total_grants || s?.grants)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{viewMode === "snapshot" ? "New Grants" : viewMode === "active" ? "All Grants" : "Ongoing Grants"}</div>
                  </div>
                </div>
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-green-300 transition-colors"
                  data-testid="kpi-grantees"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.active_grantees || s?.grantees)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Grantees</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Financial</h3>
                {s?.is_apportioned && (
                  <div className="group relative">
                    <Info size={13} className="text-amber-500 cursor-help" data-testid="apportionment-info-icon" />
                    <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-64 p-2.5 bg-slate-800 text-white text-[10px] leading-relaxed rounded-lg shadow-lg">
                      Amounts are proportionally estimated. Multi-state grants are split by block/district count in this region.
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                const isOnlyFy2425 = Array.isArray(filters.fy) && filters.fy.length === 1 && filters.fy[0] === "FY 2024-25";
                return (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2 border border-blue-100 flex flex-col justify-end" data-testid="kpi-sanctioned">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100"><IndianRupee size={11} className="text-blue-600" /></div>
                    <span className="text-[10px] text-blue-700 font-medium">Sanctioned {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"}</span>
                    {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.total_allocation_cr || s?.allocation_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-end" data-testid="kpi-cumulative-disbursement">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                    <span className="text-[10px] text-emerald-700 font-medium">Disbursement (Cumulative) till FY selected</span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.cumulative_disbursement_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-violet-50 to-white rounded-lg p-2 border border-violet-100 flex flex-col justify-end" data-testid="kpi-disbursed">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-violet-100"><Wallet size={11} className="text-violet-600" /></div>
                    <span className="text-[10px] text-violet-700 font-medium">Disbursed {viewMode === "snapshot" ? "(New)" : viewMode === "ongoing" ? "(Ongoing)" : "(New + Ongoing)"} for FY selected</span>
                    {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(s?.total_disbursement_cr || s?.disbursement_cr)}</span>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-2 border border-amber-100 flex flex-col justify-end relative group" data-testid="kpi-pending-disbursement">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-amber-100"><Clock size={11} className="text-amber-600" /></div>
                    <span className="text-[10px] text-amber-700 font-medium">Pending Disbursement</span>
                    <Info size={10} className="text-amber-400 cursor-help" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr(((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0))}</span>
                  <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 whitespace-nowrap pointer-events-none">
                    <span className="font-bold text-[11px] block mb-1">Pending Disbursement</span>
                    <span className="block">= Sanctioned − Disbursement (Cumulative)</span>
                    <span className="block mt-1">= {fmtCr(s?.total_allocation_cr || s?.allocation_cr)} − {fmtCr(s?.cumulative_disbursement_cr)}</span>
                    <span className="block font-bold mt-1">= {fmtCr(((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0))}</span>
                  </div>
                </div>
                {!isOnlyFy2425 && (
                <div className="bg-gradient-to-br from-cyan-50 to-white rounded-lg p-2 border border-dashed border-cyan-200 flex flex-col justify-end cursor-pointer hover:shadow-md transition-shadow"
                  data-testid="kpi-balance-commitment"
                  onClick={() => navigate("/financial#balance-commitment")}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-cyan-100"><Landmark size={11} className="text-cyan-600" /></div>
                    <span className="text-[10px] text-cyan-700 font-medium">Balance Commitment</span>
                    <ArrowUpRight size={10} className="text-cyan-400 ml-auto" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">₹929.03 Cr</span>
                </div>
                )}
                {!isOnlyFy2425 && (
                <div className="bg-gradient-to-br from-rose-50 to-white rounded-lg p-2 border border-rose-100 flex flex-col justify-end relative group" data-testid="kpi-cumulative-balance">
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-rose-100"><Scale size={11} className="text-rose-600" /></div>
                    <span className="text-[10px] text-rose-700 font-medium">Cumulative Balance Commitment</span>
                    <Info size={10} className="text-rose-400 cursor-help" />
                  </div>
                  <span className="text-lg font-bold text-slate-900">{fmtCr((((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0)) + 929)}</span>
                  <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 whitespace-nowrap pointer-events-none">
                    <span className="font-bold text-[11px] block mb-1">Cumulative Balance Commitment</span>
                    <span className="block">= Pending Disbursement + Balance Commitment</span>
                    <span className="block mt-1">= {fmtCr(((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0))} + ₹929.00 Cr</span>
                    <span className="block font-bold mt-1">= {fmtCr((((s?.total_allocation_cr || s?.allocation_cr) || 0) - (s?.cumulative_disbursement_cr || 0)) + 929)}</span>
                  </div>
                </div>
                )}
              </div>
                );
              })()}
            </div>

            {/* Geographic Stats */}
            <div className="bg-slate-50 rounded-lg p-2.5" data-testid="geographic-card">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Geographic</h3>
                <button onClick={() => navigate('/geographic')} className="text-[10px] text-[#B71C1C] hover:underline">Map →</button>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-blue-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.states) || '–'}</div>
                      <div className="text-[10px] text-slate-500">States & UT</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Map size={14} className="text-teal-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.districts) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Districts</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Grid3X3 size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.blocks) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Blocks</div>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 border border-amber-200 flex items-center gap-2" data-testid="asp-districts-card">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Target size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-700 leading-tight">{aspirationalData.ad?.covered || 0}/{aspirationalData.ad?.total || 0}</div>
                      <div className="text-[10px] text-slate-500">Asp. Districts</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 border border-purple-200 flex items-center gap-2 justify-center" data-testid="asp-blocks-card">
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Crosshair size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-700 leading-tight">{aspirationalData.ab?.covered || 0}/{aspirationalData.ab?.total || 0}</div>
                    <div className="text-[10px] text-slate-500">Aspirational Blocks</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* (Sanctioned by Theme moved into the left column under the India Map.) */}

      {/* Row 2: Source of Fund × Portfolio Heatmap moved to Finance page */}

      {/* ── Sankey: Sanctioned Amount Flow (hidden) ────────────────── */}

      {/* ── Inline Grants Table ─────────────────────────────────────── */}
      <div ref={grantsTableRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="exec-grants-table-section">
        <div
          className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between cursor-pointer select-none"
          onClick={() => setGrantsTableOpen(!grantsTableOpen)}
          data-testid="exec-grants-table-toggle"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Grants
              <ChevronDown size={14} className={`text-slate-600 transition-transform ${grantsTableOpen ? "rotate-180" : ""}`} />
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <p className="text-xs text-slate-500">{grantsTotal.toLocaleString()} grants</p>
              {grantsTableOpen && <span className="text-[11px] text-[#B71C1C] font-semibold">Click on Grant ID to view more details</span>}
              {selectedState && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px] font-medium" data-testid="grants-state-filter-tag">
                  {selectedState}{selectedDistrict ? ` / ${selectedDistrict}` : ""}
                </span>
              )}
            </div>
          </div>
          {grantsTableOpen && (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <form onSubmit={handleGrantsSearch} className="flex items-center gap-1.5">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
                  <input
                    type="text"
                    value={grantsSearchInput}
                    onChange={(e) => setGrantsSearchInput(e.target.value)}
                    placeholder="Search grants..."
                    className="pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-[#B71C1C]/20 focus:border-[#B71C1C]"
                    data-testid="exec-grants-search"
                  />
                  {grantsSearchInput && (
                    <button type="button" onClick={clearGrantsSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <button type="submit" className="px-2.5 py-1.5 bg-[#B71C1C] text-white rounded-lg text-xs font-medium hover:bg-[#8B1515]" data-testid="exec-grants-search-btn">Search</button>
              </form>
              <button
                onClick={handleGrantsExport}
                disabled={grantsExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                data-testid="exec-grants-export"
              >
                <Download size={13} />
                {grantsExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
          )}
        </div>

        {grantsTableOpen && (
          <>
            {grantsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
              </div>
            ) : grants.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-600">
                <FileText size={40} className="mb-2 opacity-40" />
                <p className="text-sm">No grants found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="exec-grants-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <SortTh label="Grant ID"     field="grant_id"           sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="px-3" />
                      <SortTh label="Project Title" field="project_title"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grantee"      field="grantee_name"       sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Portfolio"    field="theme"              sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Status"       field="status"             sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Sanctioned"   field="amount_recommended" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      <SortTh label="Disbursed"    field="disbursed"          sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      {selectedState && (
                        <>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-sanctioned-geo">Sanctioned ({selectedDistrict || selectedState})</th>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-disbursed-geo">Disbursed ({selectedDistrict || selectedState})</th>
                        </>
                      )}
                      <SortTh label="Start Date"   field="project_start_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="End Date"     field="project_end_date"   sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="FY"           field="fy"                 sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grant Approved Date" field="grant_approved_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g, i) => (
                      <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="py-2 px-3 font-mono whitespace-nowrap">
                          <button onClick={() => navigate(`/grant-overview?grant=${g.grant_id}`)}
                            className="text-[#B71C1C] hover:underline cursor-pointer font-medium">{g.grant_id || "–"}</button>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="font-medium text-slate-800 max-w-[200px] truncate block" title={g.project_title}>{g.project_title || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="text-slate-700 max-w-[140px] truncate block" title={g.grantee}>{g.grantee || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>{g.status || "–"}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{g.amount_cr ? fmtCr(g.amount_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-green-700 whitespace-nowrap">{g.disbursed_cr ? fmtCr(g.disbursed_cr) : "–"}</td>
                        {selectedState && (
                          <>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="sanctioned-geo-val">{g.sanctioned_geo_cr != null ? fmtCr(g.sanctioned_geo_cr) : "–"}</td>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="disbursed-geo-val">{g.disbursed_geo_cr != null ? fmtCr(g.disbursed_geo_cr) : "–"}</td>
                          </>
                        )}
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.start_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.end_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.approved_date)}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {/* Pagination */}
        {grantsTableOpen && grantsTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {((grantsPage - 1) * 50) + 1}–{Math.min(grantsPage * 50, grantsTotal)} of {grantsTotal} grants
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setGrantsPage(p => Math.max(1, p - 1))} disabled={grantsPage === 1}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-prev">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-600">Page {grantsPage} of {grantsTotalPages}</span>
              <button onClick={() => setGrantsPage(p => Math.min(grantsTotalPages, p + 1))} disabled={grantsPage === grantsTotalPages}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-next">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SortTh Sub-Component ────────────────────────────────────────── */
function SortTh({ label, field, sortBy, sortOrder, onSort, className = "" }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}
      data-testid={`exec-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? "text-[#B71C1C]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active
            ? sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}


/* ─── ThemePortfolioBars: single horizontal stacked bar (segments per theme) ── */
function ThemePortfolioBars({ data, sf }) {
  const valueKey = sf ? "allocation_cr" : "disbursed_cr";
  const sorted = [...data].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
  const total = sorted.reduce((s, d) => s + (d[valueKey] || 0), 0);

  return (
    <div className="flex flex-col gap-4 h-full" data-testid="theme-portfolio-bars">
      {/* Single stacked bar */}
      <div className="h-7 w-full rounded-lg overflow-hidden flex shadow-inner bg-slate-100" data-testid="theme-stacked-bar">
        {sorted.map((d, i) => {
          const w = total > 0 ? ((d[valueKey] || 0) / total) * 100 : 0;
          if (w <= 0) return null;
          const color = PIE_COLORS[i % PIE_COLORS.length];
          return (
            <div
              key={d.theme}
              className="h-full relative group transition-all duration-300 hover:brightness-110"
              style={{ width: `${w}%`, backgroundColor: color }}
              data-testid={`theme-segment-${d.theme}`}
              title={`${displayTheme(d.theme)}: ${d.pct}% · ₹${Number(d[valueKey] || 0).toFixed(2)} Cr`}
            >
              {/* Inline % label when the segment is wide enough */}
              {w >= 6 && (
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
                  {d.pct}%
                </span>
              )}
              {/* Hover tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-2.5 py-1.5 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                <span className="font-bold block">{displayTheme(d.theme)}</span>
                <span className="block opacity-80">₹{Number(d[valueKey] || 0).toFixed(2)} Cr · {d.pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend — 5 cols × 2 rows so all themes fit in two lines */}
      <div className="grid grid-cols-5 gap-x-3 gap-y-1.5">
        {sorted.map((d, i) => {
          const color = PIE_COLORS[i % PIE_COLORS.length];
          return (
            <div
              key={d.theme}
              className="flex items-center gap-1.5 text-[11px] min-w-0"
              title={`${displayTheme(d.theme)} · ₹${Number(d[valueKey] || 0).toFixed(2)} Cr · ${d.pct}%`}
              data-testid={`theme-legend-${d.theme}`}
            >
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
              <span className="font-medium text-slate-700 truncate flex-1">{displayTheme(d.theme)}</span>
              <span className="font-semibold text-slate-900 tabular-nums flex-shrink-0">{d.pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
